import React from 'react';
import { motion } from 'framer-motion';
import { PillIcon, DollarSignIcon, ScissorsIcon } from 'lucide-react';
export function SlowLeak() {
  return <section className="bg-[#0A0A0A] py-20 md:py-28" data-id="element-321">
      <div className="max-w-3xl mx-auto px-6" data-id="element-322">
        {/* Block A: The Slow Leak */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-323">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F5F5F0] tracking-tight mb-10" data-id="element-324">
            The Slow Leak
          </h2>

          <div className="space-y-6 text-lg text-[#9CA3AF] leading-relaxed" data-id="element-325">
            <p data-id="element-326">
              Hair loss doesn't announce itself. It's almost imperceptible month
              to month — until you're looking at a photo from three years ago
              and the difference is undeniable.
            </p>
            <p data-id="element-327">
              The worst part isn't what's happening on the outside. It's the
              mental overhead. The constant checking. The lighting calculations
              before every social situation. The way a candid photo from a bad
              angle can wreck an otherwise good day.
            </p>
          </div>

          {/* Pull Quote */}
          <div className="border-l-4 border-[#C8A04A] pl-6 my-12" data-id="element-328">
            <p className="text-xl md:text-2xl italic text-[#F5F5F0] leading-relaxed" data-id="element-329">
              "A gust of wind, and my day is ruined."
            </p>
          </div>

          <p className="text-lg text-[#9CA3AF] leading-relaxed" data-id="element-330">
            Every solution you've looked at has asked you to make a tradeoff you
            weren't willing to make.
          </p>
        </motion.div>

        {/* Block B: The Three Bad Options */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="mt-16" data-id="element-331">
          <h3 className="text-2xl md:text-3xl font-bold text-[#F5F5F0] tracking-tight mb-8" data-id="element-332">
            The Three Bad Options
          </h3>
        </motion.div>
      </div>

      {/* Cards — wider container */}
      <div className="max-w-6xl mx-auto px-6" data-id="element-333">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2" data-id="element-334">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: 0
        }} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8" data-id="element-335">
            <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mb-5" data-id="element-336">
              <PillIcon className="w-6 h-6 text-[#C8A04A]" data-id="element-337" />
            </div>
            <h4 className="text-xl font-bold text-[#F5F5F0] mb-3" data-id="element-338">
              Finasteride
            </h4>
            <p className="text-[#9CA3AF] leading-relaxed" data-id="element-339">
              Works for many men, but the side-effect profile sends thousands
              down a rabbit hole of brain fog, low libido, and sexual
              dysfunction they didn't sign up for.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8" data-id="element-340">
            <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mb-5" data-id="element-341">
              <DollarSignIcon className="w-6 h-6 text-[#C8A04A]" data-id="element-342" />
            </div>
            <h4 className="text-xl font-bold text-[#F5F5F0] mb-3" data-id="element-343">
              Hair Transplant
            </h4>
            <p className="text-[#9CA3AF] leading-relaxed" data-id="element-344">
              $10,000 to $30,000, finite donor supply, and horror stories about
              poor outcomes that are impossible to ignore once you've read them.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8" data-id="element-345">
            <div className="w-12 h-12 rounded-full bg-[#2A2A2A] flex items-center justify-center mb-5" data-id="element-346">
              <ScissorsIcon className="w-6 h-6 text-[#C8A04A]" data-id="element-347" />
            </div>
            <h4 className="text-xl font-bold text-[#F5F5F0] mb-3" data-id="element-348">Shave It</h4>
            <p className="text-[#9CA3AF] leading-relaxed" data-id="element-349">
              Some men own this. For most it feels less like a choice and more
              like a surrender.
            </p>
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="text-center text-lg italic text-[#9CA3AF] mt-12" data-id="element-350">
          These aren't good options. They're compromise options.{' '}
          <span className="text-[#F5F5F0] not-italic font-medium" data-id="element-351">
            Here's what the industry hasn't told you.
          </span>
        </motion.p>
      </div>
    </section>;
}